import { useState, type FormEvent } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { RIGHTS, KNOWN_PLANS, WORKSHOP_STATUSES, presetRightsForPosition, type AdminWorkshopDTO, type AdminWorkshopMember, type Right, type WorkshopStatus } from '@wf/shared'
import { useAuth } from '../app/auth'
import { PermissionsMatrix } from '../team/PermissionsMatrix'
import { InfoTip } from '../ui/InfoTip'
import { STATUS_LABELS, STATUS_TIP_LINES } from './saasLabels'
import { PositionSelect, WorkshopMemberRow } from './WorkshopMemberRow'
import { useAdminCreateUser, useAdminWorkshop, useUpdateWorkshop } from './useAdmin'

/** Werkstatt-Detail (Super-Admin-Drill-in): Stammdaten bearbeiten + Mitarbeiter verwalten. */
export function AdminWorkshopDetail() {
  const { user } = useAuth()
  const { id: idParam } = useParams()
  const id = Number(idParam)
  if (user && !user.isSuperadmin) return <Navigate to="/overview" replace />
  if (!Number.isInteger(id) || id <= 0) return <Navigate to="/admin" replace />
  return <DetailInner id={id} />
}

function DetailInner({ id }: { id: number }) {
  const { data, isLoading, error } = useAdminWorkshop(id)

  return (
    <section className="wf-view">
      <div className="wf-viewhead">
        <div>
          <Link to="/admin" className="wf-back">
            ← App-Verwaltung
          </Link>
          <h2 className="viewtitle">{data?.workshop.name ?? 'Werkstatt'}</h2>
          <p className="sub">Werkstatt verwalten (Betreiber)</p>
        </div>
      </div>

      {isLoading ? (
        <div className="muted">Werkstatt wird geladen…</div>
      ) : error || !data ? (
        <div className="error" role="alert">
          Werkstatt konnte nicht geladen werden.
        </div>
      ) : (
        <>
          <WorkshopSettings workshop={data.workshop} />
          <MembersSection workshopId={id} members={data.users} workshop={data.workshop} />
        </>
      )}
    </section>
  )
}

function WorkshopSettings({ workshop }: { workshop: AdminWorkshopDTO }) {
  const update = useUpdateWorkshop()
  const [name, setName] = useState(workshop.name)
  const [contact, setContact] = useState(workshop.contact ?? '')
  const [plan, setPlan] = useState(workshop.plan ?? 'test')
  const [status, setStatus] = useState<WorkshopStatus>(workshop.status)
  const [done, setDone] = useState(false)

  function save(e: FormEvent) {
    e.preventDefault()
    update.mutate(
      { id: workshop.id, body: { name: name.trim(), contact: contact.trim() || null, plan, status } },
      { onSuccess: () => setDone(true) },
    )
  }

  return (
    <form className="wf-card wf-adminform" onSubmit={save} aria-label="Stammdaten & Lizenz">
      <h3>Stammdaten &amp; Lizenz</h3>
      <div className="two">
        <label className="wf-field">
          <span className="wf-label">Name</span>
          <input className="wf-input" value={name} onChange={(e) => { setName(e.target.value); setDone(false) }} required />
        </label>
        <label className="wf-field">
          <span className="wf-label">Kontakt (E-Mail/Telefon)</span>
          <input className="wf-input" value={contact} onChange={(e) => { setContact(e.target.value); setDone(false) }} />
        </label>
      </div>
      <div className="two">
        <label className="wf-field">
          <span className="wf-label">Tarif ({workshop.userCount} / {workshop.userLimit} Nutzer)</span>
          <select className="wf-input" value={plan} onChange={(e) => { setPlan(e.target.value); setDone(false) }}>
            {KNOWN_PLANS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </label>
        <label className="wf-field">
          <span className="wf-label">
            Lizenz-Status
            <InfoTip title="Lizenz-Status" purpose="Was der Status für die Werkstatt bedeutet:" lines={STATUS_TIP_LINES} />
          </span>
          <select className="wf-input" value={status} onChange={(e) => { setStatus(e.target.value as WorkshopStatus); setDone(false) }}>
            {WORKSHOP_STATUSES.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </label>
      </div>
      {update.isError && <div className="error" role="alert">{(update.error as Error).message}</div>}
      {done && <div className="wf-adminrow__note">✓ Gespeichert.</div>}
      <div className="wf-form__actions">
        <button type="submit" className="wf-btn wf-btn--primary" disabled={update.isPending || !name.trim()}>
          Speichern
        </button>
      </div>
    </form>
  )
}

const EMPTY_MEMBER = { name: '', username: '', password: '', position: 'Mitarbeiter', email: '' }

// Super-Admin darf alles vergeben (kein Rang-Gate; Backend filtert nur cleanRights).
const ALL_GRANTABLE: ReadonlySet<Right> = new Set(RIGHTS)

function MembersSection({
  workshopId,
  members,
  workshop,
}: {
  workshopId: number
  members: AdminWorkshopMember[]
  workshop: AdminWorkshopDTO
}) {
  const create = useAdminCreateUser(workshopId)
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(EMPTY_MEMBER)
  const [caps, setCaps] = useState<Right[]>(() => presetRightsForPosition(EMPTY_MEMBER.position))
  const atLimit = workshop.userCount >= workshop.userLimit

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }))
  // Positionswechsel beim ANLEGEN übernimmt das Preset automatisch (Neuanlage → kein Datenverlust-Risiko);
  // gleiches Muster wie das Einladungs-Panel der Chef-UI (TeamPage).
  function changePosition(p: string) {
    set('position', p)
    setCaps(presetRightsForPosition(p))
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    create.mutate(
      {
        name: form.name.trim(),
        username: form.username.trim().toLowerCase(),
        password: form.password,
        position: form.position.trim() || 'Mitarbeiter',
        email: form.email.trim() || null,
        permissions: caps,
      },
      {
        onSuccess: () => {
          setForm(EMPTY_MEMBER)
          setCaps(presetRightsForPosition(EMPTY_MEMBER.position))
          setOpen(false)
        },
      },
    )
  }

  return (
    <>
      <div className="wf-sectionhead">
        <h3>Mitarbeiter</h3>
        <span className="wf-count">{members.length}</span>
        <button type="button" className="wf-btn wf-btn--sm wf-btn--primary" onClick={() => setOpen((o) => !o)} disabled={atLimit && !open}>
          {open ? '✕ Abbrechen' : '＋ Mitarbeiter'}
        </button>
      </div>

      {atLimit && !open && (
        <div className="wf-adminrow__note wf-overlimit">Seat-Limit des Tarifs erreicht — erst Tarif erhöhen, dann Mitarbeiter anlegen.</div>
      )}

      {open && (
        <form className="wf-card wf-adminform" onSubmit={submit} aria-label="Neuer Mitarbeiter">
          <div className="two">
            <label className="wf-field">
              <span className="wf-label">Name</span>
              <input className="wf-input" value={form.name} onChange={(e) => set('name', e.target.value)} required />
            </label>
            <label className="wf-field">
              <span className="wf-label">Login (Benutzername)</span>
              <input className="wf-input" value={form.username} autoCapitalize="none" autoCorrect="off" spellCheck={false}
                onChange={(e) => set('username', e.target.value.toLowerCase())} required />
            </label>
          </div>
          <div className="two">
            <label className="wf-field">
              <span className="wf-label">Passwort (min. 8)</span>
              <input className="wf-input" type="password" value={form.password} minLength={8} onChange={(e) => set('password', e.target.value)} required />
            </label>
            <label className="wf-field">
              <span className="wf-label">Position</span>
              <PositionSelect value={form.position} onChange={changePosition} disabled={create.isPending} />
            </label>
          </div>
          <label className="wf-field">
            <span className="wf-label">E-Mail (optional)</span>
            <input className="wf-input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
          </label>
          <span className="wf-label">Rechte</span>
          <PermissionsMatrix value={caps} onChange={setCaps} grantable={ALL_GRANTABLE} disabled={create.isPending} />
          {create.isError && <div className="error" role="alert">{(create.error as Error).message}</div>}
          <div className="wf-form__actions">
            <button type="submit" className="wf-btn wf-btn--primary" disabled={create.isPending || atLimit}>
              Mitarbeiter anlegen
            </button>
          </div>
        </form>
      )}

      {members.length === 0 ? (
        <div className="wf-empty">
          <div className="wf-empty__title">Noch keine Mitarbeiter</div>
        </div>
      ) : (
        <div className="wf-adminlist">
          {members.map((m) => (
            <WorkshopMemberRow key={m.id} workshopId={workshopId} member={m} />
          ))}
        </div>
      )}
    </>
  )
}
